import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import ky from 'ky'

import {
  type RequestError,
  extractHttpError,
} from '../components/api/extractHttpError'
import { API_PREFIX } from '../config'
import { logEvents } from './logEvents'

export type Settings = {
  organizationUid: string
  language:
    | 'da-DK'
    | 'en-US'
    | 'de-DE'
    | 'nb-NO'
    | 'sv-SE'
    | 'fi-FI'
    | 'nl-NL'
    | 'pl-PL'
    | 'es-ES'
    | 'pt-PT'
  provider: 'OpenAI' | 'AzureOpenAI'
  anonymize: boolean
  queuePrompts: {
    queues: string[]
    prompt: string
    lockSummaryEdit: boolean
  }[]
  modules: {
    handover: boolean
    voiceBotHandover: boolean
    previousInteractions: boolean
    liveSummary: boolean
    askCopilot: boolean
  }
}

export type SettingsResponse = {
  queues: Set<string>
  lockedQueues: Set<string>
  modules: Settings['modules']
}

const toQueueSet = (data: Settings) => {
  return {
    queues: new Set(
      data.queuePrompts.flatMap((d) => d.queues).map((q) => q.toLowerCase()),
    ),
    lockedQueues: new Set(
      data.queuePrompts
        .filter((qp) => qp.lockSummaryEdit)
        .flatMap((qp) => qp.queues)
        .map((q) => q.toLowerCase()),
    ),
    modules: data.modules,
  }
}

export const useCopilotSettings = <T = Settings>(
  apiToken: string | null,
  select?: (data: Settings) => T,
): UseQueryResult<T, RequestError> =>
  useQuery({
    select,
    enabled: !!apiToken,
    queryKey: [apiToken, 'config'],
    queryFn: async () => {
      try {
        return await ky
          .get(
            `${API_PREFIX}/co-pilot/v1/organizations/public?token=${apiToken}`,
          )
          .json<Settings>()
      } catch (error) {
        const e = await extractHttpError(error)
        console.warn(e)

        logEvents({
          type: 'error',
          name: e.name,
          message: e.message,
          cause: e.cause,
        })

        throw e
      }
    },
  })

export const useQueuesWithCopilotSettings = (
  apiToken: string | null,
): UseQueryResult<
  {
    queues: Set<string>
    lockedQueues: Set<string>
    modules: Settings['modules']
  },
  RequestError
> => useCopilotSettings(apiToken, toQueueSet)
